import {Accordion, AccordionDetails, AccordionSummary, Alert, Stack, TextField, Typography} from '@mui/material';
import {ExpandMore} from '@mui/icons-material';
import {LibraryOperationFiguresDto} from '../../../../shared/dtos/comparable-library.dto';
import {minutesToSeconds, secondsToMinutes} from '../../../../shared/services/operation-time.service';
import {DERIVED_ORIGINS, OperationFiguresDraft} from './figures';
import {MinutesField, formatMinutes, money} from './TimeFields';

const originLabel: Record<string, string> = {
  derived_job_length: 'split from the old job timer by the V43 migration',
  derived: 'split from the crew\'s time by the server',
  review: 'placed in job review',
  admin: 'entered here',
};

/**
 * An operation's time and note (legacy-record editor). Estimated time is
 * shown, not typed: it is the operation's share of the job's pool, moved in
 * the page's time distribution. Actual time is typed here; a split that the
 * old job timer gave the operation follows the estimate until it is.
 */
export function OperationFiguresFields({operationId, operationType, saved, draft, onChange, disabled, crewSize, laborRatePerHour}: {
  operationId: number; operationType: string; saved?: LibraryOperationFiguresDto | null; draft: OperationFiguresDraft;
  onChange: (next: OperationFiguresDraft) => void; disabled: boolean; crewSize?: number | null; laborRatePerHour?: number | null;
}) {
  const crew = Math.max(1, crewSize ?? 1);
  const actualMinutes = draft.actualSeconds === null ? null : secondsToMinutes(draft.actualSeconds);
  const derived = DERIVED_ORIGINS.includes(saved?.actualsOrigin ?? '');
  const estimated = saved?.estimatedMinutes ?? (saved?.estimatedCost && laborRatePerHour ? Math.round(saved.estimatedCost / laborRatePerHour * 60) : null);
  return (
    <Accordion defaultExpanded disableGutters elevation={0}>
      <AccordionSummary expandIcon={<ExpandMore/>}><Typography variant="subtitle2">{operationType} · time and note</Typography></AccordionSummary>
      <AccordionDetails sx={{px: 0}}>
        <Stack spacing={2}>
          <Stack spacing={0.5} data-review-field={`figure-estimated-${operationId}`}>
            <Typography variant="subtitle2">Estimated</Typography>
            <Typography variant="body2">
              {money(saved?.estimatedCost) || 'No price'}
              {estimated !== null ? ` · ${formatMinutes(estimated)} of labor${crew > 1 ? ` (${formatMinutes(Math.round(estimated / crew))} on the clock with a crew of ${crew})` : ''}` : ' · no time until a crew is on the job'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Moved with "Edit time distribution" on the left, or by this work item's equipment; the job's total stays.
            </Typography>
          </Stack>
          <MinutesField label="Actual time (total across the crew)" value={actualMinutes} disabled={disabled}
                        testId={`figure-actual-${operationId}`}
                        onChange={minutes => onChange({...draft, actualSeconds: minutes === null ? null : minutesToSeconds(minutes)})}/>
          {saved?.actualsOrigin && draft.actualSeconds === (saved.actualSeconds ?? null) && (
            <Alert severity={derived ? 'warning' : 'info'} icon={false} sx={{py: 0}}>
              This actual time was {originLabel[saved.actualsOrigin] ?? saved.actualsOrigin}.
              {derived ? ' It follows the estimate until replaced with what the crew spent.' : ''}
            </Alert>
          )}
          <TextField label="Operation note" multiline minRows={2} value={draft.notes} disabled={disabled}
                     inputProps={{maxLength: 2048, autoCapitalize: 'sentences', 'data-testid': `figure-notes-${operationId}`}}
                     onChange={event => onChange({...draft, notes: event.target.value})}/>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
